import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { Wrapper } from "./styles";
import CustomButton from "../CustomButton";
import DateStyled from "../Date";
import SelectStyled from "../Select";
import Text from "../Text";
import {
  Autocomplete,
  Box,
  ListSubheader,
  MenuItem,
  TextField,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { FiUploadCloud } from "react-icons/fi";
import type { FormDataProps } from "./interface";
import type { InteractionProps } from "../interfaces";
import { api } from "../../../../services/api";
import { toast } from "react-toastify";
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from "../../../../constants/toast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { addBusinessDays } from "../../../../utils/date";
import { buildInteractionPayload } from "./buildInteractionPayload";
import { useInfiniteResearchersQuery } from "../../../../services/hooks/researchers/queries/useResearchersQuery";
import { useInfiniteClientesQuery } from "../../../../services/hooks/clients/queries/useClientsQuery";
import {
  fetchExpert,
  useInfiniteExpertsQuery,
} from "../../../../services/hooks/experts/queries/useExpertQuery";
import { useInfiniteAssetsQuery } from "../../../../services/hooks/assets/queries/useAssetsQuery";
import type { FiltersDomain } from "../../../../types/domain/filters-domain";

/** Sugestões HH:mm de 30 em 30 min, de 05:00 a 23:00 (inclusive). Autocomplete freeSolo permite outro horário. */
const MEETING_HOUR_SUGGESTION_OPTIONS = (() => {
  const out: string[] = [];
  for (let minutes = 5 * 60; minutes <= 23 * 60; minutes += 30) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
  return out;
})();

/** Estilo alinhado ao `Text` dos outros campos da mesma linha. */
const meetingHourAutocompleteSx = {
  width: "100%",
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    height: "40px",
    backgroundColor: "#f1f1f1",
    "& fieldset": { borderColor: "#000" },
    "&:hover fieldset": { borderColor: "#76A6C2" },
    "&.Mui-focused fieldset": { borderColor: "#76A6C2" },
  },
  "& .MuiInputBase-input": {
    color: "#000",
    height: "40px",
    padding: "0 14px 0 14px",
    boxSizing: "border-box",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#d5d5d5",
    opacity: 1,
  },
} as const;

const INTERNATIONAL_OBS_LABELS = [
  "Swift Code",
  "Account Number/IBAN",
  "Bank Branch Address",
  "Amazon Gift Card Email",
] as const;

function buildInternationalObsFromBankData(bankData?: {
  swiftCode?: string;
  bankAccountNumber?: string;
  internationalBankBranch?: string;
  amazonGiftCardEmail?: string;
}) {
  const items = [
    { label: "Swift Code", value: bankData?.swiftCode },
    { label: "Account Number/IBAN", value: bankData?.bankAccountNumber },
    { label: "Bank Branch Address", value: bankData?.internationalBankBranch },
    { label: "Amazon Gift Card Email", value: bankData?.amazonGiftCardEmail },
  ]
    .map(({ label, value }) => {
      const trimmed = String(value ?? "").trim();
      return trimmed ? `${label}: ${trimmed}` : "";
    })
    .filter(Boolean);

  return items.join(", ");
}

function removeInternationalObsSegments(obs: string) {
  const base = String(obs ?? "");
  const pattern = new RegExp(
    `(?:^|,\\s*)(?:${INTERNATIONAL_OBS_LABELS.map((label) =>
      label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    ).join("|")}):[^,]*`,
    "g"
  );
  return base
    .replace(pattern, "")
    .replace(/^,\s*|\s*,\s*$/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function mergeObsWithInternationalData(obs: string, internationalObs: string) {
  const cleanObs = removeInternationalObsSegments(obs);
  const details = String(internationalObs ?? "").trim();
  if (!cleanObs) return details;
  if (!details) return cleanObs;
  return `${cleanObs}, ${details}`;
}

const RESEARCHERS_FOR_SELECT_FILTERS: FiltersDomain = {
  page: 1,
  take: 50,
  search: "",
  sort: "name",
  order: "ASC",
};

const CLIENTS_FOR_SELECT_FILTERS: FiltersDomain = {
  page: 1,
  take: 50,
  search: "",
  sort: "name",
  order: "ASC",
};

const EXPERTS_FOR_SELECT_FILTERS: FiltersDomain = {
  page: 1,
  take: 50,
  search: "",
  sort: "name",
  order: "ASC",
};

const ASSETS_FOR_SELECT_FILTERS: FiltersDomain = {
  page: 1,
  take: 50,
  search: "",
};

interface NewMeetingProps {
  initialData?: InteractionProps | null;
  edit?: boolean;
  onSaved?: () => void;
}

const EMPTY_FORM: FormDataProps = {
  researcherId: "",
  clientId: "",
  projectNumber: "",
  asanaProjectUrl: "",
  date: "",
  hour: "",

  expert_id: "",
  expert: "",
  expert_company: "",

  fee: "",
  duration: "",
  assets: [],
  analysts: "",

  payment_deadline: "",
  payment_day: "",

  status: 1,
  currency: null,
  delay: null,
  interaction_model: null,
  interaction_type: null,
  proactive: null,
  persona: null,
  payment_method: null,
  feedback: null,

  delay_value: "",

  full_name: "",
  cpf: "",

  bank: "",
  agency: "",
  account: "",
  pix: "",
  swiftCode: "",
  bankAccountNumber: "",
  internationalBankBranch: "",
  amazonGiftCardEmail: "",

  birthday: "",
  rg: "",
  country: "",
  cep: "",
  address: "",
  city: "",

  real_duration: "",
  obs: "",
};
type ExpertDetails = {
  id: number;
  name?: string;
  cpf?: string;
  personType?: "natural_person" | "legal_person" | "international_person";
  legalData?: {
    companyName?: string;
    cnpj?: string;
  };
  naturalData?: {
    birthdate?: string;
    rg?: string;
    country?: string;
    cep?: string;
    street?: string;
    city?: string;
  };
  bankData?: {
    bank?: string;
    bankBranch?: string;
    currentAccount?: string;
    pix?: string;
    swiftCode?: string;
    bankAccountNumber?: string;
    internationalBankBranch?: string;
    amazonGiftCardEmail?: string;
  };
  professionalData?: {
    currentCompany?: string;
    currency?: "BRL" | "USD" | "EUR";
    proactive?: "no" | "yesOffer" | "yesSearch";
    value?: string;
  };
};

const NewMeeting: React.FC<NewMeetingProps> = ({
  initialData,
  edit,
  onSaved,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [removeExistingAttachment, setRemoveExistingAttachment] =
    useState(false);

  const [formData, setFormData] = useState<FormDataProps>(() => ({
    ...EMPTY_FORM,
  }));

  const isDelay = formData.delay === 2 || formData.delay === 3;

  const isTransfer = formData.payment_method === 1;

  const isPF = formData.persona === 1;
  const isInternational = formData.persona === 3;

  const [loading, setLoading] = useState(false);
  const [expertSearchTerm, setExpertSearchTerm] = useState("");
  const [debouncedExpertSearchTerm, setDebouncedExpertSearchTerm] =
    useState("");
  const expertAutofillRequestIdRef = useRef(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedExpertSearchTerm(expertSearchTerm);
    }, 300);
    return () => clearTimeout(timeout);
  }, [expertSearchTerm]);

  const {
    data: researchersInfinite,
    isLoading: researchersLoading,
    isError: researchersError,
    hasNextPage: researchersHasNextPage,
    fetchNextPage: fetchNextResearchersPage,
    isFetchingNextPage: researchersFetchingNextPage,
  } = useInfiniteResearchersQuery(RESEARCHERS_FOR_SELECT_FILTERS);

  const researcherRows =
    researchersInfinite?.pages.flatMap((page) => page.data) ?? [];
  const selectedResearcherId = formData.researcherId
    ? String(formData.researcherId)
    : "";
  const orphanResearcher =
    selectedResearcherId &&
    !researcherRows.some((r) => String(r.id) === selectedResearcherId)
      ? {
          id: Number(selectedResearcherId),
          label:
            edit &&
            initialData?.researcherId != null &&
            String(initialData.researcherId) === selectedResearcherId
              ? initialData.ra || `Researcher #${selectedResearcherId}`
              : `Researcher #${selectedResearcherId}`,
        }
      : null;

  const {
    data: clientsInfinite,
    isLoading: clientsLoading,
    isError: clientsError,
    hasNextPage: clientsHasNextPage,
    fetchNextPage: fetchNextClientsPage,
    isFetchingNextPage: clientsFetchingNextPage,
  } = useInfiniteClientesQuery(CLIENTS_FOR_SELECT_FILTERS);

  const clientRows = clientsInfinite?.pages.flatMap((p) => p.data) ?? [];
  const selectedClientId = formData.clientId ? String(formData.clientId) : "";
  const orphanClient =
    selectedClientId &&
    !clientRows.some((c) => String(c.id) === selectedClientId)
      ? {
          id: Number(selectedClientId),
          label:
            edit && initialData?.clientId != null
              ? String(initialData.clientId) === selectedClientId
                ? initialData.cliente || `Cliente #${selectedClientId}`
                : `Cliente #${selectedClientId}`
              : `Cliente #${selectedClientId}`,
        }
      : null;

  /** Busca só com dígitos = filtro por ID exato (evita LIKE %9342% na listagem). */
  const expertSearchAsNumericId = useMemo(() => {
    const t = debouncedExpertSearchTerm.trim();
    if (!/^\d+$/.test(t)) return null;
    const n = Number(t);
    if (!Number.isFinite(n) || n <= 0 || n > Number.MAX_SAFE_INTEGER)
      return null;
    return Math.trunc(n);
  }, [debouncedExpertSearchTerm]);

  const expertFilters = useMemo<FiltersDomain>(
    () => ({
      ...EXPERTS_FOR_SELECT_FILTERS,
      search: debouncedExpertSearchTerm.trim(),
    }),
    [debouncedExpertSearchTerm]
  );

  const {
    data: expertsInfinite,
    isLoading: expertsLoading,
    isError: expertsError,
    hasNextPage: expertsHasNextPage,
    fetchNextPage: fetchNextExpertsPage,
    isFetchingNextPage: expertsFetchingNextPage,
  } = useInfiniteExpertsQuery(expertFilters, {
    enabled: expertSearchAsNumericId === null,
  });

  const { data: expertByIdForSearch, isLoading: expertByIdSearchLoading } =
    useQuery(
      ["expert-autocomplete-by-id", expertSearchAsNumericId],
      () => fetchExpert(expertSearchAsNumericId!),
      {
        enabled: expertSearchAsNumericId !== null,
        retry: false,
        staleTime: 60_000,
        /** 404 = ID inexistente: lista vazia, sem label de “erro ao carregar”. */
        useErrorBoundary: false,
      }
    );

  const expertRowsFromInfinite =
    expertsInfinite?.pages.flatMap((p) => p.data) ?? [];

  const expertRows = useMemo(() => {
    if (expertSearchAsNumericId !== null) {
      if (
        expertByIdForSearch &&
        Number(expertByIdForSearch.id) === expertSearchAsNumericId
      ) {
        return [expertByIdForSearch];
      }
      return [];
    }
    return expertRowsFromInfinite;
  }, [expertSearchAsNumericId, expertByIdForSearch, expertRowsFromInfinite]);

  const expertsListLoading =
    expertSearchAsNumericId !== null ? expertByIdSearchLoading : expertsLoading;
  const expertsListError =
    expertSearchAsNumericId !== null ? false : expertsError;
  const expertsHasNextPageEffective =
    expertSearchAsNumericId !== null ? false : Boolean(expertsHasNextPage);
  const selectedExpertId = formData.expert_id ? String(formData.expert_id) : "";
  const orphanExpert =
    selectedExpertId &&
    !expertRows.some((exp) => String(exp.id) === selectedExpertId)
      ? {
          id: Number(selectedExpertId),
          label:
            edit && initialData?.expertId != null
              ? String(initialData.expertId) === selectedExpertId
                ? initialData.nome || `Expert #${selectedExpertId}`
                : `Expert #${selectedExpertId}`
              : `Expert #${selectedExpertId}`,
        }
      : null;

  const {
    data: assetsInfinite,
    isLoading: assetsLoading,
    isError: assetsError,
    hasNextPage: assetsHasNextPage,
    fetchNextPage: fetchNextAssetsPage,
    isFetchingNextPage: assetsFetchingNextPage,
  } = useInfiniteAssetsQuery(ASSETS_FOR_SELECT_FILTERS);

  const assetRows =
    assetsInfinite?.pages.flatMap((p: any) => p.data ?? []) ?? [];
  const orphanAssetIds = useMemo(
    () =>
      formData.assets.filter(
        (id) => !assetRows.some((a: any) => String(a.id) === String(id))
      ),
    [formData.assets, assetRows]
  );

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      if (name === "researcherId") {
        return {
          ...prev,
          researcherId: value === "" ? "" : String(value),
        };
      }

      if (name === "clientId") {
        return {
          ...prev,
          clientId: value === "" ? "" : String(value),
        };
      }

      if (name === "projectNumber" || name === "asanaProjectUrl") {
        return {
          ...prev,
          [name]: typeof value === "string" ? value : String(value),
        };
      }

      if (name === "hour") {
        return {
          ...prev,
          hour: typeof value === "string" ? value : String(value),
        };
      }

      if (name === "assets") {
        const raw = (e.target as EventTarget & { value: unknown }).value;
        const next = Array.isArray(raw)
          ? raw.map(String)
          : raw === "" || raw == null
          ? []
          : [String(raw)];
        return {
          ...prev,
          assets: next,
        };
      }

      if (name === "expert_id") {
        const selectedId = value === "" ? "" : String(value);
        const selectedExpert = expertRows.find(
          (exp) => String(exp.id) === selectedId
        );
        const nextState = {
          ...prev,
          expert_id: selectedId,
          // Mantemos esse campo para o payload/snapshot no backend.
          expert: selectedExpert?.name ?? "",
        };
        if (selectedId === "") {
          // Se limpou expert, limpa também os campos auto-preenchidos por expert.
          return {
            ...nextState,
            expert_company: "",
            currency: null,
            proactive: null,
            fee: "",
            payment_method: null,
            bank: "",
            agency: "",
            account: "",
            pix: "",
            swiftCode: "",
            bankAccountNumber: "",
            internationalBankBranch: "",
            amazonGiftCardEmail: "",
            persona: null,
            full_name: "",
            cpf: "",
            birthday: "",
            rg: "",
            country: "",
            cep: "",
            address: "",
            city: "",
          };
        }
        return nextState;
      }

      const updated = {
        ...prev,
        [name]:
          value === ""
            ? null
            : Number.isNaN(Number(value))
            ? value
            : Number(value),
      };

      // 🔹 Atraso
      if (name === "delay" && updated.delay !== 2 && updated.delay !== 3) {
        updated.delay_value = "";
      }

      // 🔹 Meio de pagamento
      if (name === "payment_method") {
        updated.bank = "";
        updated.agency = "";
        updated.account = "";
        updated.pix = "";
      }

      // 🔹 Persona
      if (name === "persona" && updated.persona !== 1) {
        updated.birthday = "";
        updated.rg = "";
        updated.country = "";
        updated.cep = "";
        updated.address = "";
        updated.city = "";
      }

      if (name === "persona" && updated.persona === 3) {
        updated.full_name = "";
        updated.cpf = "";
        updated.payment_method = null;
        updated.bank = "";
        updated.agency = "";
        updated.account = "";
        updated.pix = "";
        updated.delay = null;
        updated.delay_value = "";
        updated.real_duration = "";
      }

      return updated;
    });
  };

  useEffect(() => {
    if (edit) return;
    const selectedId = formData.expert_id ? Number(formData.expert_id) : null;
    if (!selectedId || Number.isNaN(selectedId)) return;

    const requestId = ++expertAutofillRequestIdRef.current;
    const fillExpertData = async () => {
      try {
        const { data } = await api.get<ExpertDetails>(`/experts/${selectedId}`);
        // Ignora resposta antiga se usuário já trocou de expert
        if (requestId !== expertAutofillRequestIdRef.current) return;

        const currencyCode =
          data.professionalData?.currency === "BRL"
            ? 1
            : data.professionalData?.currency === "USD"
            ? 2
            : data.professionalData?.currency === "EUR"
            ? 3
            : null;

        const personaCode =
          data.personType === "natural_person"
            ? 1
            : data.personType === "legal_person"
            ? 2
            : data.personType === "international_person"
            ? 3
            : null;

        const paymentMethodCode = data.bankData?.pix
          ? 2
          : data.bankData?.bank ||
            data.bankData?.bankBranch ||
            data.bankData?.currentAccount
          ? 1
          : null;

        setFormData((prev) => ({
          ...prev,
          expert_id: String(data.id ?? selectedId),
          expert: data.name ?? prev.expert,
          expert_company:
            data.professionalData?.currentCompany ?? prev.expert_company,
          currency: currencyCode ?? prev.currency,
          fee:
            data.professionalData?.value != null
              ? String(data.professionalData.value)
              : prev.fee,
          payment_method: paymentMethodCode ?? prev.payment_method,
          bank: data.bankData?.bank ?? prev.bank,
          agency: data.bankData?.bankBranch ?? prev.agency,
          account: data.bankData?.currentAccount ?? prev.account,
          pix: data.bankData?.pix ?? prev.pix,
          swiftCode: data.bankData?.swiftCode ?? prev.swiftCode,
          bankAccountNumber:
            data.bankData?.bankAccountNumber ?? prev.bankAccountNumber,
          internationalBankBranch:
            data.bankData?.internationalBankBranch ??
            prev.internationalBankBranch,
          amazonGiftCardEmail:
            data.bankData?.amazonGiftCardEmail ?? prev.amazonGiftCardEmail,
          persona: personaCode ?? prev.persona,
          obs:
            personaCode === 3
              ? mergeObsWithInternationalData(
                  prev.obs,
                  buildInternationalObsFromBankData(data.bankData)
                )
              : prev.obs,
          full_name: data.legalData?.companyName ?? data.name ?? prev.full_name,
          cpf: data.legalData?.cnpj ?? data.cpf ?? prev.cpf,
          birthday: data.naturalData?.birthdate ?? prev.birthday,
          rg: data.naturalData?.rg ?? prev.rg,
          country: data.naturalData?.country ?? prev.country,
          cep: data.naturalData?.cep ?? prev.cep,
          address: data.naturalData?.street ?? prev.address,
          city: data.naturalData?.city ?? prev.city,
        }));
      } catch {
        // Não bloqueia o fluxo se autofill falhar; usuário ainda pode preencher manualmente.
      }
    };

    void fillExpertData();
  }, [formData.expert_id, edit]);
  const buildMultipartPayload = (
    payload: Record<string, unknown>,
    selectedFile: File
  ) => {
    const form = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        form.append(key, JSON.stringify(value));
        return;
      }
      form.append(key, String(value));
    });
    form.append("file", selectedFile);
    return form;
  };

  const getAttachmentUrl = (attachmentPath?: string | null) => {
    if (!attachmentPath) return null;
    if (attachmentPath.startsWith("http")) return attachmentPath;
    const base = api.defaults.baseURL ?? "";
    return `${base.replace(/\/$/, "")}/${attachmentPath.replace(/^\//, "")}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const rid = Number(formData.researcherId);
    const cid = Number(formData.clientId);
    if (!Number.isFinite(rid) || rid < 1 || !Number.isFinite(cid) || cid < 1) {
      toast.error(
        "Informe Researcher e Cliente válidos.",
        TOAST_ERROR_DEFAULT_OPTIONS
      );
      return;
    }
    if (!formData.projectNumber?.trim()) {
      toast.error("Informe o número do projeto.", TOAST_ERROR_DEFAULT_OPTIONS);
      return;
    }
    const asanaTrim = formData.asanaProjectUrl?.trim() ?? "";
    if (asanaTrim) {
      try {
        const u = new URL(asanaTrim);
        if (u.protocol !== "http:" && u.protocol !== "https:") {
          throw new Error("invalid protocol");
        }
      } catch {
        toast.error(
          "Informe um link válido da task do Asana (http ou https).",
          TOAST_ERROR_DEFAULT_OPTIONS
        );
        return;
      }
    }
    if (!formData.date?.trim()) {
      toast.error("Informe a data da reunião.", TOAST_ERROR_DEFAULT_OPTIONS);
      return;
    }

    setLoading(true);

    try {
      const payload = buildInteractionPayload(formData) as Record<
        string,
        unknown
      >;
      if (edit && removeExistingAttachment) {
        payload.remove_attachment = 1;
      }
      const requestBody = file ? buildMultipartPayload(payload, file) : payload;
      const requestConfig = file
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : undefined;

      if (edit && initialData?.id) {
        await api.patch(
          `/interactions/${initialData.id}`,
          requestBody,
          requestConfig
        );
        toast.success(
          "Reunião atualizada com sucesso.",
          TOAST_SUCCESS_DEFAULT_OPTIONS
        );
      } else {
        await api.post(`/interactions`, requestBody, requestConfig);
        toast.success(
          "Reunião criada com sucesso.",
          TOAST_SUCCESS_DEFAULT_OPTIONS
        );
      }
      setFile(null);
      setRemoveExistingAttachment(false);
      onSaved?.();
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err), TOAST_ERROR_DEFAULT_OPTIONS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialData) {
      setFormData({ ...EMPTY_FORM });
      setFile(null);
      setRemoveExistingAttachment(false);
      return;
    }

    setFile(null);
    setRemoveExistingAttachment(false);
    setFormData({
      ...EMPTY_FORM,

      researcherId:
        initialData.researcherId != null
          ? String(initialData.researcherId)
          : "",
      clientId:
        initialData.clientId != null ? String(initialData.clientId) : "",
      projectNumber: (
        initialData.projectNumber ??
        initialData.projeto ??
        ""
      ).trim(),
      asanaProjectUrl: initialData.asanaProjectUrl?.trim() ?? "",
      date: initialData.data ?? "",
      hour: initialData.hora ?? "",

      expert_id:
        initialData.expertId != null ? String(initialData.expertId) : "",
      expert: initialData.nome ?? "",
      expert_company: initialData.empresa ?? "",

      duration: initialData.duracao != null ? String(initialData.duracao) : "",
      assets: (initialData.assetIds ?? []).map(String),
      analysts: initialData.analistas ?? "",

      interaction_model:
        initialData.interactionModelCode ??
        (initialData.modeloInteracao === "1on1"
          ? 1
          : initialData.modeloInteracao === "Small Group"
          ? 2
          : initialData.modeloInteracao === "Group Meeting"
          ? 3
          : initialData.modeloInteracao === "Conversa Guiada"
          ? 4
          : initialData.modeloInteracao === "Consultoria"
          ? 5
          : null),

      interaction_type:
        initialData.interactionTypeCode ??
        (initialData.tipoInteracao === "Video call"
          ? 1
          : initialData.tipoInteracao === "Presencial"
          ? 2
          : null),

      currency:
        initialData.currencyCode ??
        (initialData.moeda === "BRL"
          ? 1
          : initialData.moeda === "USD"
          ? 2
          : initialData.moeda === "EUR"
          ? 3
          : null),

      status: initialData.statusCode ?? 1,
      delay: initialData.delayCode ?? null,
      proactive: initialData.proactiveCode ?? null,
      feedback: initialData.feedbackCode ?? null,
      persona: initialData.personaCode ?? null,
      payment_method:
        initialData.paymentMethodCode ??
        (initialData.tedOuPix === "PIX"
          ? 2
          : initialData.tedOuPix === "Transferencia"
          ? 1
          : null),

      fee: initialData.feeCredito != null ? String(initialData.feeCredito) : "",

      full_name: initialData.nomeCompleto ?? initialData.payerFullName ?? "",
      cpf: initialData.cpf ?? "",

      payment_deadline: initialData.deadlinePgto ?? "",
      payment_day: initialData.paymentDay ?? "",

      delay_value:
        initialData.delayMinutesValue != null
          ? String(initialData.delayMinutesValue)
          : "",

      bank: initialData.banco ?? "",
      agency: initialData.agencia ?? "",
      account: initialData.conta ?? "",
      pix: initialData.pix ?? "",

      birthday: initialData.dataNascimento ?? "",
      rg: initialData.rg ?? "",
      country: initialData.pais ?? "",
      address: initialData.endereco ?? "",
      city: initialData.municipio ?? "",
      cep: initialData.cep ?? "",

      real_duration:
        initialData.duracaoReal != null ? String(initialData.duracaoReal) : "",
      obs: initialData.observacao ?? "",
    });
  }, [initialData]);
  useEffect(() => {
    if (formData.status === 2 && formData.date) {
      const deadline = addBusinessDays(formData.date, 10);

      setFormData((prev) => ({
        ...prev,
        payment_deadline: deadline,
      }));
    }
  }, [formData.status, formData.date]);

  const sanitizeFileName = (file: File | null) => {
    if (!file) return null;
    const newName = file.name.replace(/\s+/g, "_"); // remove espaços
    return new File([file], newName, { type: file.type });
  };

  const FileDropZone = ({
    label,
    onSelect,
    selectedFile,
  }: {
    label: string;
    onSelect: (file: File | null) => void;
    selectedFile: File | null;
  }) => (
    <Box
      onDrop={(e) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
          onSelect(sanitizeFileName(e.dataTransfer.files[0]));
        }
      }}
      onDragOver={(e) => e.preventDefault()}
      onClick={() => document.getElementById(label)?.click()}
      sx={{
        border: "2px dashed #ccc",
        width: "50%",
        height: "80px",
        borderRadius: "12px",
        alignSelf: "center",
        p: 1,
        textAlign: "center",
        cursor: "pointer",
        backgroundColor: "#fafafa",
        color: "#666",
        fontSize: "0.6rem",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: "#76A6C2",
          backgroundColor: "#f1f1f1",
          color: "#000",
        },
      }}
    >
      <FiUploadCloud size={15} color="#76A6C2" style={{ marginBottom: -3 }} />
      {selectedFile ? (
        <span style={{ color: "#76A6C2", fontWeight: 500 }}>
          ✅ {selectedFile.name}
        </span>
      ) : (
        <span>
          Arraste seu arquivo PDF aqui <br />
          <strong>ou clique para selecionar</strong>
        </span>
      )}
      <input
        id={label}
        type="file"
        accept="application/pdf"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0] || null;
          onSelect(sanitizeFileName(f));
        }}
      />
    </Box>
  );

  return (
    <Wrapper>
      {edit ? <h1>Editar Reunião</h1> : <h1>Registrar Nova Reunião</h1>}

      <form onSubmit={handleSubmit}>
        <div className="row">
          <Box sx={{ flex: "1 1 220px", minWidth: 160, maxWidth: 340 }}>
            <SelectStyled
              id="researcherId"
              name="researcherId"
              label={
                researchersError
                  ? "Researcher (erro ao carregar)"
                  : researchersLoading
                  ? "Researcher (carregando...)"
                  : "Researcher (RA)"
              }
              value={formData.researcherId}
              onChange={handleChange}
              disabled={researchersLoading}
              infiniteScroll={{
                hasNextPage: Boolean(researchersHasNextPage),
                onLoadMore: () => {
                  void fetchNextResearchersPage();
                },
                isFetchingNextPage: researchersFetchingNextPage,
              }}
            >
              <MenuItem value="">
                <em>Selecione</em>
              </MenuItem>
              {orphanResearcher && (
                <MenuItem value={String(orphanResearcher.id)}>
                  {orphanResearcher.label}
                </MenuItem>
              )}
              {researcherRows.map((r) => (
                <MenuItem key={r.id} value={String(r.id)}>
                  {r.name}
                </MenuItem>
              ))}
              {researchersFetchingNextPage && (
                <ListSubheader sx={{ fontSize: "0.65rem", lineHeight: 2 }}>
                  Carregando mais…
                </ListSubheader>
              )}
            </SelectStyled>
          </Box>
          <Box sx={{ flex: "1 1 220px", minWidth: 160, maxWidth: 340 }}>
            <SelectStyled
              id="clientId"
              name="clientId"
              label={
                clientsError
                  ? "Cliente (erro ao carregar)"
                  : clientsLoading
                  ? "Cliente (carregando...)"
                  : "Cliente"
              }
              value={formData.clientId}
              onChange={handleChange}
              disabled={clientsLoading}
              infiniteScroll={{
                hasNextPage: Boolean(clientsHasNextPage),
                onLoadMore: () => {
                  void fetchNextClientsPage();
                },
                isFetchingNextPage: clientsFetchingNextPage,
              }}
            >
              <MenuItem value="">
                <em>Selecione</em>
              </MenuItem>
              {orphanClient && (
                <MenuItem value={String(orphanClient.id)}>
                  {orphanClient.label}
                </MenuItem>
              )}
              {clientRows.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
              {clientsFetchingNextPage && (
                <ListSubheader sx={{ fontSize: "0.65rem", lineHeight: 2 }}>
                  Carregando mais…
                </ListSubheader>
              )}
            </SelectStyled>
          </Box>
          <Box sx={{ flex: "0 1 185px", minWidth: 150, maxWidth: 210 }}>
            <DateStyled
              id="date"
              name="date"
              label="Data"
              value={formData.date}
              onChange={handleChange}
            />
          </Box>
          {/* Status antes de Hora no DOM + order no flex: lista do Autocomplete não fica por baixo do Select na mesma linha. */}
          <Box
            sx={{ order: 5, flex: "0 0 148px", minWidth: 120, maxWidth: 168 }}
          >
            <SelectStyled
              id="status"
              name="status"
              label="Status"
              value={formData.status}
              onChange={handleChange}
            >
              <MenuItem key={1} value={1}>
                Not Done
              </MenuItem>
              <MenuItem key={2} value={2}>
                Done
              </MenuItem>
              <MenuItem key={3} value={3}>
                Canceled Client
              </MenuItem>
              <MenuItem key={4} value={4}>
                On Hold
              </MenuItem>
              <MenuItem key={5} value={5}>
                Reagendar
              </MenuItem>
              <MenuItem key={6} value={6}>
                No show client
              </MenuItem>
              <MenuItem key={7} value={7}>
                No show expert
              </MenuItem>
              <MenuItem key={8} value={8}>
                Canceled Expert
              </MenuItem>
            </SelectStyled>
          </Box>
          <Box
            sx={{ order: 4, flex: "0 0 118px", minWidth: 96, maxWidth: 132 }}
          >
            <Autocomplete
              freeSolo
              fullWidth
              size="small"
              openOnFocus
              slotProps={{
                popper: {
                  // style vence o merge interno (ex.: width) e sobe acima do modal / Select.
                  style: { zIndex: 50000 },
                  sx: { zIndex: "50000 !important" },
                },
                listbox: {
                  sx: {
                    maxHeight: 280,
                    overflowY: "auto",
                  },
                },
              }}
              options={MEETING_HOUR_SUGGESTION_OPTIONS}
              value={formData.hour}
              inputValue={formData.hour}
              onChange={(_e, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  hour:
                    newValue === null || newValue === undefined
                      ? ""
                      : String(newValue),
                }));
              }}
              onInputChange={(_e, newInputValue) => {
                setFormData((prev) => ({ ...prev, hour: newInputValue }));
              }}
              sx={meetingHourAutocompleteSx}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="hour"
                  name="hour"
                  label="Hora (BRT)"
                  placeholder="HH:mm"
                />
              )}
            />
          </Box>
        </div>
        <div className="row">
          <Box sx={{ flex: "0 0 140px", minWidth: 100, maxWidth: 160 }}>
            <Text
              id="projectNumber"
              name="projectNumber"
              label="Nº do projeto"
              value={formData.projectNumber}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Text
              id="asanaProjectUrl"
              name="asanaProjectUrl"
              label="Link do Projeto"
              value={formData.asanaProjectUrl}
              onChange={handleChange}
            />
          </Box>
        </div>
        <div className="row">
          <SelectStyled
            id="expert_id"
            name="expert_id"
            label={
              expertsListError
                ? "Expert (erro ao carregar)"
                : expertsListLoading
                ? "Expert (carregando...)"
                : "Expert (ID - nome)"
            }
            value={formData.expert_id}
            onChange={handleChange}
            disabled={expertsListLoading}
            infiniteScroll={{
              hasNextPage: expertsHasNextPageEffective,
              onLoadMore: () => {
                void fetchNextExpertsPage();
              },
              isFetchingNextPage: expertsFetchingNextPage,
            }}
          >
            <ListSubheader
              disableSticky
              sx={{ bgcolor: "#f1f1f1", py: 1 }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <input
                type="text"
                value={expertSearchTerm}
                placeholder="Buscar por nome ou ID"
                onChange={(e) => setExpertSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: "1px solid #c8c8c8",
                  background: "#fff",
                  color: "#000",
                  fontSize: "14px",
                  height: "40px",
                }}
              />
            </ListSubheader>
            <MenuItem value="">
              <em>Selecione</em>
            </MenuItem>
            {orphanExpert && (
              <MenuItem value={String(orphanExpert.id)}>
                {orphanExpert.label}
              </MenuItem>
            )}
            {expertRows.map((exp) => (
              <MenuItem key={exp.id} value={String(exp.id)}>
                {`${exp.id} - ${exp.name}`}
              </MenuItem>
            ))}
            {expertsFetchingNextPage && (
              <ListSubheader sx={{ fontSize: "0.65rem", lineHeight: 2 }}>
                Carregando mais…
              </ListSubheader>
            )}
          </SelectStyled>
          <Text
            id="expert_company"
            name="expert_company"
            label="Empresa"
            value={formData.expert_company}
            onChange={handleChange}
          />
          <Text
            id="duration"
            name="duration"
            label="Duração (min)"
            value={formData.duration}
            onChange={handleChange}
          />
        </div>
        <div className="row">
          <Box sx={{ flex: "0 0 190px", minWidth: 160, maxWidth: 210 }}>
            <SelectStyled
              id="interaction_model"
              name="interaction_model"
              label="Modelo de Interação"
              value={formData.interaction_model}
              onChange={handleChange}
            >
              <MenuItem key={1} value={1}>
                {" "}
                1on1{" "}
              </MenuItem>
              <MenuItem key={2} value={2}>
                {" "}
                Small Group{" "}
              </MenuItem>
              <MenuItem key={3} value={3}>
                {" "}
                Group Meeting{" "}
              </MenuItem>
              <MenuItem key={4} value={4}>
                {" "}
                Conversa Guiada{" "}
              </MenuItem>
              <MenuItem key={5} value={5}>
                {" "}
                Consultoria{" "}
              </MenuItem>
            </SelectStyled>
          </Box>
          <Box sx={{ flex: "0 0 160px", minWidth: 150, maxWidth: 200 }}>
            <SelectStyled
              id="interaction_type"
              name="interaction_type"
              label="Tipo de Interação"
              value={formData.interaction_type}
              onChange={handleChange}
            >
              <MenuItem key={1} value={1}>
                {" "}
                Video Call{" "}
              </MenuItem>
              <MenuItem key={2} value={2}>
                {" "}
                Presencial{" "}
              </MenuItem>
            </SelectStyled>
          </Box>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <SelectStyled
              multiple
              id="assets"
              name="assets"
              label={
                assetsError
                  ? "Assets (erro ao carregar)"
                  : assetsLoading
                  ? "Assets (carregando...)"
                  : "Assets"
              }
              value={formData.assets}
              onChange={handleChange}
              disabled={assetsLoading}
              renderValue={(selected) => {
                const ids = selected as string[];
                if (!ids.length) {
                  return (
                    <em
                      style={{
                        color: "#999",
                        fontStyle: "normal",
                        marginLeft: "8%",
                      }}
                    >
                      Selecione um ou mais
                    </em>
                  );
                }
                const parts = ids.map((id) => {
                  const row = assetRows.find(
                    (a: { id: number }) => String(a.id) === id
                  );
                  return row?.fullName ?? `#${id}`;
                });
                const txt = parts.join(", ");
                return txt.length > 96 ? `${txt.slice(0, 93)}…` : txt;
              }}
              infiniteScroll={{
                hasNextPage: Boolean(assetsHasNextPage),
                onLoadMore: () => {
                  void fetchNextAssetsPage();
                },
                isFetchingNextPage: assetsFetchingNextPage,
              }}
            >
              {orphanAssetIds.map((id) => (
                <MenuItem key={`orphan-asset-${id}`} value={String(id)}>
                  {`Asset #${id}`}
                </MenuItem>
              ))}
              {assetRows.map((a: { id: number; fullName: string }) => (
                <MenuItem key={a.id} value={String(a.id)}>
                  {a.fullName}
                </MenuItem>
              ))}
              {assetsFetchingNextPage && (
                <ListSubheader sx={{ fontSize: "0.65rem", lineHeight: 2 }}>
                  Carregando mais…
                </ListSubheader>
              )}
            </SelectStyled>
          </Box>
        </div>
        <div className="row">
          <Box sx={{ flex: 1, minWidth: 400 }}>
            <Text
              id="analysts"
              name="analysts"
              label="Analistas"
              value={formData.analysts}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ flex: "0 0 272px", minWidth: 216, maxWidth: 320 }}>
            <SelectStyled
              id="proactive"
              name="proactive"
              label="Proativo"
              value={formData.proactive}
              onChange={handleChange}
            >
              <MenuItem key={1} value={1}>
                {" "}
                Não{" "}
              </MenuItem>
              <MenuItem key={2} value={2}>
                {" "}
                Sim, oferta{" "}
              </MenuItem>
              <MenuItem key={3} value={3}>
                {" "}
                Sim, pesquisa{" "}
              </MenuItem>
            </SelectStyled>
          </Box>
        </div>
        <div className="row">
          <>
            <SelectStyled
              id="currency"
              name="currency"
              label="Moeda"
              value={formData.currency}
              onChange={handleChange}
            >
              <MenuItem key={1} value={1}>
                {" "}
                BRL{" "}
              </MenuItem>
              <MenuItem key={2} value={2}>
                {" "}
                USD{" "}
              </MenuItem>
              <MenuItem key={3} value={3}>
                {" "}
                EUR{" "}
              </MenuItem>
            </SelectStyled>
            <Text
              id="fee"
              name="fee"
              label="Fee BRL"
              value={formData.fee}
              onChange={handleChange}
            />
            <DateStyled
              id="payment_deadline"
              name="payment_deadline"
              label="Deadline Pagamento"
              value={formData.payment_deadline}
              onChange={handleChange}
            />
            <DateStyled
              id="payment_day"
              name="payment_day"
              label="Data de pagamento"
              value={formData.payment_day}
              onChange={handleChange}
            />
          </>
          <SelectStyled
            id="persona"
            name="persona"
            label="Tipo de Pagamento"
            value={formData.persona}
            onChange={handleChange}
          >
            <MenuItem key={1} value={1}>
              {" "}
              PF{" "}
            </MenuItem>
            <MenuItem key={2} value={2}>
              {" "}
              PJ{" "}
            </MenuItem>
            <MenuItem key={3} value={3}>
              {" "}
              Internacional{" "}
            </MenuItem>
          </SelectStyled>
          {isInternational && (
            <Text
              id="country"
              name="country"
              label="País"
              value={formData.country}
              onChange={handleChange}
            />
          )}
        </div>
        {!isInternational && (
          <div className="row">
            <Text
              id="full_name"
              name="full_name"
              label="Nome Completo ou Razão Social"
              value={formData.full_name}
              onChange={handleChange}
            />
            <Text
              id="cpf"
              name="cpf"
              label="CPF ou CNPJ"
              value={formData.cpf}
              onChange={handleChange}
            />
            <SelectStyled
              id="payment_method"
              name="payment_method"
              label="Meio de Pagamento"
              value={formData.payment_method}
              onChange={handleChange}
            >
              <MenuItem key={1} value={1}>
                {" "}
                Transferencia{" "}
              </MenuItem>
              <MenuItem key={2} value={2}>
                {" "}
                PIX{" "}
              </MenuItem>
            </SelectStyled>

            {isTransfer ? (
              <>
                <Text
                  id="bank"
                  name="bank"
                  label="Banco"
                  value={formData.bank}
                  onChange={handleChange}
                />
                <Text
                  id="agency"
                  name="agency"
                  label="Agencia"
                  value={formData.agency}
                  onChange={handleChange}
                />
                <Text
                  id="account"
                  name="account"
                  label="Conta"
                  value={formData.account}
                  onChange={handleChange}
                />
              </>
            ) : (
              <>
                <Text
                  id="pix"
                  name="pix"
                  label="PIX"
                  value={formData.pix}
                  onChange={handleChange}
                />
              </>
            )}
          </div>
        )}
        {isPF && !isInternational ? (
          <div className="row">
            <Text
              id="birthday"
              name="birthday"
              label="Data de Nascimento"
              value={formData.birthday}
              onChange={handleChange}
            />

            <Text
              id="rg"
              name="rg"
              label="RG"
              value={formData.rg}
              onChange={handleChange}
            />

            <Text
              id="country"
              name="country"
              label="País"
              value={formData.country}
              onChange={handleChange}
            />
            <Text
              id="cep"
              name="cep"
              label="CEP"
              value={formData.cep}
              onChange={handleChange}
            />
            <Text
              id="address"
              name="address"
              label="Endereço"
              value={formData.address}
              onChange={handleChange}
            />
            <Text
              id="city"
              name="city"
              label="Município"
              value={formData.city}
              onChange={handleChange}
            />
          </div>
        ) : (
          <>
            {!isInternational && (
              <>
                {edit &&
                  initialData?.attachmentPath &&
                  !removeExistingAttachment && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 8,
                        fontSize: "12px",
                      }}
                    >
                      <a
                        href={
                          getAttachmentUrl(initialData.attachmentPath) ?? "#"
                        }
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: "#76A6C2",
                          textDecoration: "underline",
                        }}
                      >
                        {initialData.attachmentOriginalName ?? "Anexo atual"}
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          setRemoveExistingAttachment(true);
                          setFile(null);
                        }}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#b54b4b",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        Remover anexo
                      </button>
                    </div>
                  )}
                {edit && removeExistingAttachment && (
                  <div
                    style={{
                      marginBottom: 8,
                      fontSize: "12px",
                      color: "#b54b4b",
                    }}
                  >
                    O anexo atual será removido ao salvar.
                    <button
                      type="button"
                      onClick={() => setRemoveExistingAttachment(false)}
                      style={{
                        marginLeft: 8,
                        background: "transparent",
                        border: "none",
                        color: "#76A6C2",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      Desfazer
                    </button>
                  </div>
                )}
                <FileDropZone
                  label="generalFile"
                  onSelect={(selected) => {
                    setFile(selected);
                    if (selected) setRemoveExistingAttachment(false);
                  }}
                  selectedFile={file}
                />
              </>
            )}
          </>
        )}

        <div className="row">
          {!isInternational && (
            <>
              <Text
                id="real_duration"
                name="real_duration"
                label="Duração real (min)"
                value={formData.real_duration}
                onChange={handleChange}
              />
              <SelectStyled
                id="delay"
                name="delay"
                label="Atraso"
                value={formData.delay}
                onChange={handleChange}
              >
                <MenuItem key={1} value={1}>
                  {" "}
                  Não{" "}
                </MenuItem>
                <MenuItem key={2} value={2}>
                  {" "}
                  Sim, analista{" "}
                </MenuItem>
                <MenuItem key={3} value={3}>
                  {" "}
                  Sim, expert{" "}
                </MenuItem>
              </SelectStyled>
              {isDelay && (
                <Text
                  id="delay_value"
                  name="delay_value"
                  label="Atraso (min)"
                  value={formData.delay_value}
                  onChange={handleChange}
                />
              )}
            </>
          )}
          <SelectStyled
            id="feedback"
            name="feedback"
            label="Feedback"
            value={formData.feedback}
            onChange={handleChange}
          >
            <MenuItem key={1} value={1}>
              {" "}
              Não{" "}
            </MenuItem>
            <MenuItem key={2} value={2}>
              {" "}
              Sim, bom{" "}
            </MenuItem>
            <MenuItem key={3} value={3}>
              {" "}
              Sim, ruim{" "}
            </MenuItem>
          </SelectStyled>
        </div>

        <Text
          id="obs"
          name="obs"
          label="Observações"
          value={formData.obs}
          onChange={handleChange}
        />

        <CustomButton
          id="suport"
          type="submit"
          text={loading ? "Enviando..." : "Salvar"}
          disabled={loading}
        />
      </form>
    </Wrapper>
  );
};

export default NewMeeting;
